import Script from 'next/script';
import { forwardRef, useImperativeHandle, useEffect, useRef } from 'react';

export type VerifyCallbackType = (arg: boolean) => void;

declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: (token: string) => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  appearance?: 'always' | 'execute' | 'interaction-only';
}

export interface TurnstileHandle {
  reset: () => void;
}

interface TurnstileProps {
  callback: (token: string) => void;
  errorCallback: () => void;
  expiredCallback: (token: string) => void;
}

const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
  ({ callback, errorCallback, expiredCallback }: TurnstileProps, ref) => {
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';
    const turnstileID = 'tc-turnstile';
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    const renderTurnstileWidget = () => {
      if (window.turnstile && containerRef.current && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'light',
          size: 'normal',
          callback: (token: string) => {
            callback(token);
          },
          'error-callback': () => {
            errorCallback();
          },
          'expired-callback': (token: string) => {
            expiredCallback(token);
          },
        });
      }
    };

    useEffect(() => {
      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, []);

    return (
      <>
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          async
          defer
          onReady={renderTurnstileWidget}
        />
        <div ref={containerRef} id={turnstileID}></div>
      </>
    );
  }
);

Turnstile.displayName = 'Turnstile';
export default Turnstile;
